import { useState } from "react";
import QuickLinks from "./QuickLinks";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTheme } from "@/hooks/ThemeContext";

interface HotIssue {
  id: number;
  category: string;
  categoryColor: string;
  title: string;
  time: string;
  views: number;
}

interface CommunityPost {
  id: number;
  rank: number;
  title: string;
  likes: number;
  isHot: boolean;
}

interface SportsArticle {
  id: number;
  title: string;
  sport_type: string;
  source: string;
  views: number;
  likes: number;
  comments: number;
  created_at: string;
}

interface MainContentProps {
  hotIssues?: HotIssue[];
  hotIssuesLoading?: boolean;
  communityPosts?: CommunityPost[];
  communityPostsLoading?: boolean;
  sportsArticles?: SportsArticle[];
  sportsArticlesLoading?: boolean;
}

function SectionHeader({
  icon,
  title,
  action,
}: {
  icon: string;
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3 border-b border-background-300">
      <div className="flex items-center gap-2">
        <span className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center text-primary-500">
          <i className={icon} />
        </span>
        <h2 className="text-xs md:text-sm font-bold text-foreground-700">{title}</h2>
      </div>
      {action}
    </div>
  );
}

function TabBar({
  tabs,
  activeTab,
  onTabChange,
}: {
  tabs: string[];
  activeTab: string;
  onTabChange: (tab: string) => void;
}) {
  return (
    <div className="flex items-center gap-1 px-3 md:px-4 py-2 border-b border-background-300 overflow-x-auto">
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => onTabChange(tab)}
          className={`px-2 py-1 text-[10px] md:text-xs font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer ${
            activeTab === tab
              ? "text-primary-400 bg-primary-500/10"
              : "text-foreground-600 hover:text-foreground-400 hover:bg-background-200/50"
          }`}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}

const tabToSportType: Record<string, string | null> = {
  "전체": null,
  "축구분석": "soccer",
  "배구분석": "volleyball",
  "농구분석": "basketball",
  "야구분석": "baseball",
  "이벤트": "event",
};

const sportLabelMap: Record<string, string> = {
  soccer: "축구",
  baseball: "야구",
  basketball: "농구",
  volleyball: "배구분석",
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function SkeletonList({ count = 4 }: { count?: number }) {
  return (
    <div className="px-3 md:px-4 py-2 space-y-1 min-h-[160px]">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-2 py-1.5">
          <div className="w-8 h-3 rounded bg-background-200 animate-pulse" />
          <div className="flex-1 h-3 rounded bg-background-200 animate-pulse" />
          <div className="w-10 h-3 rounded bg-background-200 animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function SportsAnalysisSection({ articles, loading }: { articles?: SportsArticle[]; loading?: boolean }) {
  const [activeTab, setActiveTab] = useState("전체");
  const tabs = ["전체", "축구분석", "야구분석", "농구분석", "배구분석", "이벤트"];
  const data = articles || [];

  const filtered = (() => {
    const type = tabToSportType[activeTab];
    let result;
    if (!type) result = data;
    else if (type === "event") result = data.filter((a) => a.sport_type === "event");
    else result = data.filter((a) => a.sport_type === type);
    return result.slice(0, 4);
  })();

  return (
    <div className="bg-background-100 rounded-lg border border-background-500 overflow-hidden">
      <SectionHeader
        icon="ri-bar-chart-box-line"
        title="스포츠 분석"
        action={
          <Link
            to="/sports"
            className="text-[10px] md:text-xs text-foreground-600 cursor-pointer hover:text-foreground-400 transition-colors"
          >
            더보기 &gt;
          </Link>
        }
      />
      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      {loading ? (
        <SkeletonList />
      ) : (
        <div className="px-3 md:px-4 pt-2 pb-0 space-y-1 min-h-[160px]">
          {filtered.length === 0 ? (
            <div className="py-8 text-center">
              <span className="w-6 h-6 flex items-center justify-center text-foreground-600 mx-auto mb-2">
                <i className="ri-inbox-line" />
              </span>
              <p className="text-[11px] text-foreground-600">등록된 기사가 없습니다</p>
            </div>
          ) : (
            filtered.map((article) => (
              <Link
                key={article.id}
                to={`/sports/article/${article.id}`}
                className="flex items-center gap-2 py-1.5 border-b border-background-300/50 last:border-0 cursor-pointer hover:bg-background-200/30 transition-colors rounded px-1"
              >
                <span className="px-1.5 py-0.5 text-[9px] md:text-[10px] font-bold rounded flex-shrink-0 bg-primary-500/20 text-primary-400">
                  {sportLabelMap[article.sport_type] || article.sport_type}
                </span>
                <span className="flex-1 text-[10px] md:text-[11px] text-foreground-600 truncate">
                  {article.title}
                </span>
                <span className="text-[9px] md:text-[10px] text-foreground-600 flex-shrink-0 w-10 text-right">
                  {formatDate(article.created_at)}
                </span>
                <span className="text-[9px] md:text-[10px] text-foreground-600 flex-shrink-0 w-10 text-right hidden sm:block">
                  {article.views}
                </span>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function CommunitySection({ posts, loading }: { posts?: CommunityPost[]; loading?: boolean }) {
  const [activeTab, setActiveTab] = useState("전체");
  const tabs = ["전체", "추천", "게시", "댓글"];
  const data = posts || [];

  return (
    <div className="bg-background-100 rounded-lg border border-background-500 overflow-hidden">
      <SectionHeader
        icon="ri-chat-smile-3-line"
        title="커뮤니티 인기글"
        action={
          <Link
            to="/community"
            className="text-[10px] md:text-xs text-foreground-600 cursor-pointer hover:text-foreground-400 transition-colors"
          >
            더보기 &gt;
          </Link>
        }
      />
      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      {loading ? (
        <SkeletonList />
      ) : (
        <div className="px-3 md:px-4 pt-2 pb-0 space-y-1 min-h-[160px]">
          {data.length === 0 ? (
            <div className="py-8 text-center">
              <span className="w-6 h-6 flex items-center justify-center text-foreground-600 mx-auto mb-2">
                <i className="ri-inbox-line" />
              </span>
              <p className="text-[11px] text-foreground-600">등록된 게시글이 없습니다</p>
            </div>
          ) : (
            data.map((post) => (
              <Link
                key={post.id}
                to={`/post/${post.id}`}
                className={`flex items-center gap-2 py-1.5 border-b border-background-300/50 last:border-0 cursor-pointer transition-colors rounded px-1 ${
                  post.isHot ? "hover:bg-primary-500/5" : "hover:bg-background-200/30"
                }`}
              >
                <div className="flex items-center gap-1 flex-shrink-0 w-10">
                  <span className={`w-5 text-center text-[10px] md:text-xs font-bold flex-shrink-0 ${post.isHot ? "text-primary-400" : "text-foreground-600"}`}>
                    {post.rank}
                  </span>
                  {post.isHot && (
                    <span className="px-1 text-[7px] font-bold bg-primary-500 text-white rounded-full">
                      HOT
                    </span>
                  )}
                </div>
                <span className={`flex-1 text-[10px] md:text-[11px] truncate ${post.isHot ? "text-foreground-700 font-bold" : "text-foreground-600"}`}>
                  {post.title}
                </span>
                <span className="flex items-center gap-1 text-[9px] md:text-[10px] text-foreground-600 flex-shrink-0">
                  <i className="ri-thumb-up-line" />
                  {post.likes}
                </span>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function LivescoreSection() {
  const { settings, loading } = useSiteSettings();
  const { isDark } = useTheme();
  const defaultSrc = isDark
    ? '//bigwins.liventv.com/worldcupb/livescore/livescore/'
    : '//bigwins.liventv.com/worldcupw/livescore/livescore/';

  let src: string;
  if (isDark) {
    src = settings.livescore_iframe_src || defaultSrc;
  } else {
    src = settings.livescore_iframe_src_light || defaultSrc;
  }

  if (loading) {
    return (
      <section className="bg-background-100 py-4 md:py-5">
        <div className="max-w-[1540px] mx-auto px-3 md:px-4">
          <div className="animate-pulse bg-background-200 rounded-lg" style={{ height: 500 }} />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-background-100 py-4 md:py-5">
      <div className="max-w-[1540px] mx-auto px-3 md:px-4">
        <div className="rounded-lg overflow-hidden border border-background-300">
          <iframe
            allowFullScreen
            frameBorder="0"
            name="player"
            scrolling="yes"
            src={src}
            title="스포츠 중계"
            style={{ overflow: 'hidden', width: '100%', height: 2500 }}
          />
        </div>
      </div>
    </section>
  );
}

export default function MainContent({ hotIssues, communityPosts, sportsArticles, hotIssuesLoading, communityPostsLoading, sportsArticlesLoading }: MainContentProps) {
  return (
    <div className="flex flex-col gap-3 min-w-0">
      <QuickLinks />

      {/* Sports Analysis & Community */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SportsAnalysisSection articles={sportsArticles} loading={sportsArticlesLoading} />
        <CommunitySection posts={communityPosts} loading={communityPostsLoading} />
      </div>

      {/* Livescore iframe */}
      <LivescoreSection />
    </div>
  );
}